/*
  Two States — launch intro (web port of the Coffee Journal app's LaunchIntro.kt + Bean3D.kt).
  An outline sketch of the world draws itself, flows into the Two States bean (coastlines become the
  rim, the middle of the map becomes the S crease), the bean turns solid and spins in 3D, the wordmark
  rises, then the site is revealed. Once per browser session; skipped for reduced motion; tap to skip.
  World points: assets/ts-world.txt (sampled from the app's Natural Earth outlines by tools/sample-world.ps1).
*/
const TOTAL_MS = 3500;
const LEAVE_MS = 600;
const MAP_ASPECT = 360 / (84 + 56); // WorldProjection.ASPECT
const BEAN_ASPECT = 248.732 / 323.352;
const INK = [246, 240, 229]; // cream on espresso night

const root = document.documentElement;
const overlay = document.querySelector('[data-intro-overlay]');
const script = document.querySelector('script[data-world]');

const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a, b, t) => a + (b - a) * t;

// FastOutSlowInEasing = cubic-bezier(0.4, 0, 0.2, 1)
function fastOutSlowIn(x) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  let lo = 0, hi = 1, t = x;
  for (let i = 0; i < 24; i++) {
    t = (lo + hi) / 2;
    const m = 1 - t;
    const bx = 3 * m * m * t * 0.4 + 3 * m * t * t * 0.2 + t * t * t;
    if (bx < x) lo = t; else hi = t;
  }
  const m = 1 - t;
  return 3 * m * t * t * 1 + t * t * t; // y control points 0 and 1
}

function finishNow() {
  try { sessionStorage.setItem('ts-intro', '1'); } catch (e) { /* storage blocked: intro may replay */ }
  if (!overlay) { root.classList.remove('ts-intro-on'); document.dispatchEvent(new Event('ts:intro-done')); return; }
  overlay.classList.add('is-active', 'is-leaving');
  root.classList.remove('ts-intro-on');
  document.dispatchEvent(new Event('ts:intro-done'));
  setTimeout(() => overlay.classList.remove('is-active'), LEAVE_MS);
}

/* ---------- Morph: paired points on the world outline and on the bean ---------- */
function mulberry32(a) {
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function creasePoint(u) {
  const cubic = (p0, p1, p2, p3, t) => { const m = 1 - t; return m * m * m * p0 + 3 * m * m * t * p1 + 3 * m * t * t * p2 + t * t * t * p3; };
  if (u < 0.5) { const t = u * 2; return [cubic(0, -0.35, 0.3, 0, t), cubic(-1, -0.654, -0.385, 0, t)]; }
  const t = (u - 0.5) * 2;
  return [cubic(0, -0.3, 0.32, 0, t), cubic(0, 0.385, 0.654, 1, t)];
}

function buildMorph(text) {
  const sx = [], sy = [], ring = [];
  text.trim().split('|').forEach((r, ri) => r.split(';').forEach((p) => {
    const c = p.indexOf(',');
    sx.push(parseFloat(p.slice(0, c))); sy.push(parseFloat(p.slice(c + 1))); ring.push(ri);
  }));
  const n = sx.length;
  const srcNext = new Int32Array(n).map((_, i) => (i + 1 < n && ring[i + 1] === ring[i] ? i + 1 : -1));
  const nS = Math.floor(n * 0.22);
  const idx = [...Array(n).keys()];
  const creaseSrc = idx.slice().sort((a, b) => Math.abs((sx[a] - 0.5) * MAP_ASPECT) - Math.abs((sx[b] - 0.5) * MAP_ASPECT)).slice(0, nS).sort((a, b) => sy[a] - sy[b]);
  const inCrease = new Set(creaseSrc);
  const TAU = Math.PI * 2;
  const angleKey = (i) => { const a = Math.atan2(sy[i] - 0.5, (sx[i] - 0.5) * MAP_ASPECT) + Math.PI / 2; return ((a % TAU) + TAU) % TAU; };
  const rimSrc = idx.filter((i) => !inCrease.has(i)).sort((a, b) => angleKey(a) - angleKey(b));
  const tx = new Float32Array(n), ty = new Float32Array(n), delay = new Float32Array(n);
  const dstNext = new Int32Array(n).fill(-1);
  const rnd = mulberry32(7);
  const nE = rimSrc.length;
  rimSrc.forEach((i, j) => {
    const phi = -Math.PI / 2 + (TAU * j) / nE;
    tx[i] = Math.cos(phi); ty[i] = Math.sin(phi);
    dstNext[i] = rimSrc[(j + 1) % nE];
    delay[i] = (0.55 * j) / nE + rnd() * 0.12;
  });
  creaseSrc.forEach((i, j) => {
    const u = j / Math.max(1, nS - 1);
    const [x, y] = creasePoint(u);
    tx[i] = x; ty[i] = y;
    dstNext[i] = j + 1 < nS ? creaseSrc[j + 1] : -1;
    delay[i] = 0.3 + 0.4 * u + rnd() * 0.1;
  });
  return { n, sx, sy, tx, ty, srcNext, dstNext, delay };
}

/* ---------- The 3D bean (Bean3D.drawBean3D) ---------- */
const C = {
  lightHi: '#E2BF96', lightLo: '#A9703F', darkHi: '#5A3A28', darkLo: '#1E130D',
  sideNear: [0x5e, 0x3d, 0x2a], sideFar: [0x26, 0x17, 0x0f], groove: 'rgba(20, 11, 7, ',
};

function creasePath(ctx, cx, cy, rx, ry) {
  const px = (x) => cx + x * rx, py = (y) => cy + y * ry;
  ctx.moveTo(px(0), py(-1));
  ctx.bezierCurveTo(px(-0.35), py(-0.654), px(0.3), py(-0.385), px(0), py(0));
  ctx.bezierCurveTo(px(-0.3), py(0.385), px(0.32), py(0.654), px(0), py(1));
}

function drawBean3D(ctx, cx, cy, ry, angleDeg, alpha) {
  if (alpha <= 0) return;
  const rx = ry * BEAN_ASPECT;
  const theta = (angleDeg / 180) * Math.PI;
  const c = Math.cos(theta), s = Math.sin(theta);
  const sxs = Math.max(Math.abs(c), 0.03);
  const depth = rx * 0.3;
  ctx.save();
  ctx.globalAlpha = alpha;

  // Floor shadow.
  const sw = rx * (1.3 + 0.25 * Math.abs(s));
  const sh = ctx.createRadialGradient(cx, cy + ry * 1.06, 0, cx, cy + ry * 1.06, sw);
  sh.addColorStop(0, 'rgba(0,0,0,0.28)'); sh.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = sh;
  ctx.beginPath(); ctx.ellipse(cx, cy + ry * 1.06, sw, ry * 0.12, 0, 0, Math.PI * 2); ctx.fill();

  // Extruded body: slices from the far face to the near one.
  for (let k = 0; k <= 18; k++) {
    const f = k / 18;
    const ox = -s * depth * (1 - f);
    const col = C.sideFar.map((v, i) => Math.round(lerp(v, C.sideNear[i], f)));
    ctx.fillStyle = `rgb(${col[0]},${col[1]},${col[2]})`;
    ctx.beginPath(); ctx.ellipse(cx + ox, cy, rx * sxs, ry, 0, 0, Math.PI * 2); ctx.fill();
  }

  ctx.save();
  ctx.translate(cx, cy); ctx.scale(c >= 0 ? sxs : -sxs, 1); ctx.translate(-cx, -cy);
  const outline = () => { ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); };
  if (c < 0) {
    const g = ctx.createRadialGradient(cx - rx * 0.3, cy - ry * 0.4, 0, cx - rx * 0.3, cy - ry * 0.4, ry * 1.4);
    g.addColorStop(0, C.darkHi); g.addColorStop(1, C.darkLo);
    ctx.fillStyle = g; outline(); ctx.fill();
    ctx.restore(); ctx.restore();
    return;
  }
  // Light-roast half (left) and dark-roast half (right), split by the S crease.
  let g = ctx.createLinearGradient(cx - rx, cy - ry, cx + rx * 0.2, cy + ry);
  g.addColorStop(0, C.lightHi); g.addColorStop(1, C.lightLo);
  ctx.fillStyle = g;
  ctx.beginPath(); creasePath(ctx, cx, cy, rx, ry); ctx.ellipse(cx, cy, rx, ry, 0, Math.PI / 2, (3 * Math.PI) / 2, false); ctx.closePath(); ctx.fill();
  g = ctx.createLinearGradient(cx - rx * 0.2, cy - ry, cx + rx, cy + ry);
  g.addColorStop(0, C.darkHi); g.addColorStop(1, C.darkLo);
  ctx.fillStyle = g;
  ctx.beginPath(); creasePath(ctx, cx, cy, rx, ry); ctx.ellipse(cx, cy, rx, ry, 0, Math.PI / 2, -Math.PI / 2, true); ctx.closePath(); ctx.fill();
  // Dome lighting and the light sweep as it turns.
  ctx.save();
  outline(); ctx.clip();
  g = ctx.createRadialGradient(cx - rx * 0.35, cy - ry * 0.45, 0, cx - rx * 0.35, cy - ry * 0.45, ry * 1.55);
  g.addColorStop(0, 'rgba(255,255,255,0.30)'); g.addColorStop(0.45, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = g; ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2);
  if (s > 0) { ctx.fillStyle = `rgba(255,255,255,${0.1 * s})`; ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2); }
  if (s < 0) { ctx.fillStyle = `rgba(0,0,0,${-0.18 * s})`; ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2); }
  ctx.restore();
  // Crease groove with a lit edge.
  ctx.lineCap = 'round';
  ctx.strokeStyle = C.groove + '0.85)'; ctx.lineWidth = rx * 0.085;
  ctx.beginPath(); creasePath(ctx, cx, cy, rx, ry); ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.22)'; ctx.lineWidth = rx * 0.03;
  ctx.beginPath(); creasePath(ctx, cx + rx * 0.045, cy, rx, ry); ctx.stroke();
  // Specular highlight and rim.
  const hx = cx - rx * 0.42, hy = cy - ry * 0.5;
  g = ctx.createRadialGradient(hx, hy, 0, hx, hy, rx * 0.32);
  g.addColorStop(0, 'rgba(255,255,255,0.55)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.ellipse(hx, hy, rx * 0.32, ry * 0.22, 0, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = 'rgba(26,16,11,0.55)'; ctx.lineWidth = rx * 0.025;
  outline(); ctx.stroke();
  ctx.restore();
  ctx.restore();
}

/* ---------- Run ---------- */
function run() {
  if (!overlay || !root.classList.contains('ts-intro-on')) return;
  overlay.classList.add('is-active');
  const canvas = overlay.querySelector('canvas');
  const word = overlay.querySelector('[data-intro-word]');
  const ctx = canvas.getContext('2d');
  let morph = null;
  let done = false;
  const end = () => { if (!done) { done = true; finishNow(); } };
  overlay.addEventListener('click', end);
  addEventListener('keydown', (e) => { if (e.key === 'Escape') end(); });

  const src = script && script.dataset.world;
  if (src) fetch(src).then((r) => (r.ok ? r.text() : Promise.reject())).then((t) => { morph = buildMorph(t); }).catch(() => {});

  let W = 0, H = 0, dpr = 1;
  const size = () => {
    dpr = Math.min(2, devicePixelRatio || 1);
    W = innerWidth; H = innerHeight;
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  };
  size();
  addEventListener('resize', size);

  // Give the map a moment to load; without it, go straight to the bean (as the app does).
  const t0 = performance.now();
  let offset = null;
  const frame = (now) => {
    if (done) return;
    if (offset === null) {
      if (!morph && now - t0 < 900) { requestAnimationFrame(frame); return; }
      offset = morph ? now : now - 2000;
    }
    const t = now - offset;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const beanRy = Math.min(H * 0.16, (W * 0.28) / BEAN_ASPECT);
    const beanCy = H * 0.4;
    const cx = W / 2;

    const lineFade = 1 - clamp((t - 2150) / 300);
    if (morph && lineFade > 0) {
      const m = morph;
      const mapW = Math.min(W * 0.92, H * 0.55 * MAP_ASPECT);
      const mapH = mapW / MAP_ASPECT;
      const left = (W - mapW) / 2, top = beanCy - mapH / 2;
      const rx = beanRy * BEAN_ASPECT;
      const drawn = Math.min(m.n, Math.floor(clamp(t / 1000) * m.n));
      const morphT = clamp((t - 950) / 1200);
      const px = new Float32Array(m.n), py = new Float32Array(m.n), loc = new Float32Array(m.n);
      for (let i = 0; i < m.n; i++) {
        const l = fastOutSlowIn(clamp((morphT - m.delay[i] * 0.45) / 0.55));
        loc[i] = l;
        px[i] = lerp(left + m.sx[i] * mapW, cx + m.tx[i] * rx, l);
        py[i] = lerp(top + m.sy[i] * mapH, beanCy + m.ty[i] * beanRy, l);
      }
      // Map lines join points that haven't left; bean lines join points that have landed;
      // in between, points fly free as ink particles, so nothing stretches into streaks.
      ctx.lineCap = 'round';
      ctx.beginPath();
      for (let i = 0; i < drawn; i++) {
        const j = m.srcNext[i];
        if (j >= 0 && j < drawn && loc[i] < 0.01 && loc[j] < 0.01) { ctx.moveTo(px[i], py[i]); ctx.lineTo(px[j], py[j]); }
      }
      ctx.strokeStyle = `rgba(${INK},${0.62 * lineFade})`; ctx.lineWidth = 1.3; ctx.stroke();
      ctx.beginPath();
      for (let i = 0; i < drawn; i++) {
        const k = m.dstNext[i];
        if (k >= 0 && loc[i] > 0.97 && loc[k] > 0.97) { ctx.moveTo(px[i], py[i]); ctx.lineTo(px[k], py[k]); }
      }
      ctx.strokeStyle = `rgba(${INK},${0.85 * lineFade})`; ctx.lineWidth = 1.8; ctx.stroke();
      ctx.fillStyle = `rgba(${INK},${0.75 * lineFade})`;
      for (let i = 0; i < drawn; i++) if (loc[i] >= 0.01 && loc[i] <= 0.97) { ctx.beginPath(); ctx.arc(px[i], py[i], 1.3, 0, Math.PI * 2); ctx.fill(); }
    }
    // The solid bean fills the drawn outline face-on, then makes one full turn to show its depth.
    const beanA = clamp((t - 2000) / 350);
    if (beanA > 0) drawBean3D(ctx, cx, beanCy, beanRy, 360 * fastOutSlowIn(clamp((t - 2300) / 1100)), beanA);

    const wordA = clamp((t - 2550) / 450);
    if (word) {
      word.style.top = `${beanCy + beanRy * 1.32}px`;
      word.style.opacity = String(wordA);
      word.style.transform = `translateY(${18 * (1 - wordA)}px)`;
    }
    if (t >= TOTAL_MS) { end(); return; }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

run();
